import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  Card,
  CardContent,
  CardActions,
  useTheme,
  useMediaQuery,
  Avatar,
  Tooltip,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  ListItemSecondaryAction,
  InputAdornment,
  Badge,
} from '@mui/material';
import {
  PhoneAndroid as PhoneIcon,
  Add as AddIcon,
  Refresh as RefreshIcon,
  VpnKey as PasswordIcon,
  PowerSettingsNew as PowerIcon,
  Search as SearchIcon,
  CheckCircle as ActiveIcon,
  Cancel as InactiveIcon,
  Info as InfoIcon,
  DevicesOther as DeviceIcon,
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { useAuthWithCustomerOverride } from '../../hooks/useAuthWithCustomerOverride';
import api from '../../services/api';
import { useCustomerFilter } from '../../hooks/useCustomerFilter';
import { useNavigate } from 'react-router-dom';
import ChargePreviewDialog from '../Common/ChargePreviewDialog';
import { getCustomerParams } from '../../utils/customerParams';
import errorLogger from '../../services/errorLogger.service';

// Create logger for this component
const logger = errorLogger.createComponentLogger('Softphones');

interface SoftphoneUser {
  id: number;
  username: string | null;
  ringotel_id?: string | null;
  extension: string;
  extension_desc: string;
  customer_id: string;
  customer_name: string;
  email: string;
  has_softphone: boolean;
  disabled: number;
  state?: number;
  state_text?: string;
  state_color?: string;
  last_activity?: string;
  devices?: Device[];
  device_count?: number;
}

interface Device {
  id: string;
  name: string;
  type: string;
  last_seen: string;
  active: boolean;
}

interface SoftphoneStats {
  total_extensions: number;
  total_softphones: number;
  active_softphones: number;
  connected_devices: number;
}

const Softphones: React.FC = () => {
  const authOverride = useAuthWithCustomerOverride();
  const { member, isLoginAsCustomer } = authOverride;
  const customerFilter = useCustomerFilter();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  
  // Check for login token on initialization
  const urlParams = new URLSearchParams(window.location.search);
  const hasLoginToken = urlParams.has('lat');
  const hasSessionData = !!sessionStorage.getItem('loginAsCustomer');
  const [waitingForLoginAs] = useState(() => {
    const shouldWait = hasLoginToken && !hasSessionData;
    if (shouldWait) {
      logger.debug('Initializing with waiting=true due to login token');
    }
    return shouldWait;
  });
  
  const [loading, setLoading] = useState(true);
  const [softphones, setSoftphones] = useState<SoftphoneUser[]>([]);
  const [stats, setStats] = useState<SoftphoneStats>({
    total_extensions: 0,
    total_softphones: 0,
    active_softphones: 0,
    connected_devices: 0,
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedUser, setSelectedUser] = useState<SoftphoneUser | null>(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [resetPasswordOpen, setResetPasswordOpen] = useState(false);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [chargePreviewOpen, setChargePreviewOpen] = useState(false);
  const [chargeDetails, setChargeDetails] = useState<any>(null);
  const [chargePreviewLoading, setChargePreviewLoading] = useState(false);
  const [chargePreviewError, setChargePreviewError] = useState<string | null>(null);
  const [pendingExtension, setPendingExtension] = useState<string>('');
  const [pendingForce, setPendingForce] = useState(false);

  useEffect(() => {
    if (!member || !['admin', 'master_admin', 'reseller_admin'].includes(member.memberType)) {
      return;
    }
    
    // Check for login token
    const urlParams = new URLSearchParams(window.location.search);
    const hasLoginToken = urlParams.has('lat');
    const sessionData = sessionStorage.getItem('loginAsCustomer');
    
    // If we have a login token but no session data, wait
    if (hasLoginToken && !sessionData) {
      logger.debug('Have login token but no session data - waiting...');
      return;
    }
    
    // If session data exists, verify it matches member
    if (sessionData) {
      try {
        const parsed = JSON.parse(sessionData);
        if (parsed.customerId && parsed.customerId !== member.customerID) {
          logger.debug('Session/member mismatch, waiting for sync...');
          return;
        }
      } catch (e) {
        logger.error('Error parsing session data:', e);
      }
    }
    
    // Initial fetch with delay
    const timer = setTimeout(() => {
      fetchSoftphones();
    }, 200);
    
    // Set up auto-refresh every 30 seconds
    const interval = setInterval(() => {
      fetchSoftphones();
    }, 30000);
    
    return () => {
      clearTimeout(timer);
      clearInterval(interval);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [member?.customerID, isLoginAsCustomer]);
  
  // Listen for login-as-customer changes to update tab title and refetch data
  useEffect(() => {
    const handleLoginAsChange = (event: CustomEvent) => {
      if (event.detail && event.detail.customerId && event.detail.customerName) {
        document.title = `${event.detail.customerId} - ${event.detail.customerName} - Softphones`;
        // Also refetch data when login-as changes
        setTimeout(() => {
          if (member) fetchSoftphones();
        }, 100);
      }
    };

    window.addEventListener('loginAsCustomerChanged', handleLoginAsChange as EventListener);

    // Set initial title if in login-as mode
    if (isLoginAsCustomer && member) {
      const customerId = (member as any).customerID || (member as any).customer_id;
      const customerName = (member as any).loginAsCustomerName || '';
      if (customerId && customerName) {
        document.title = `${customerId} - ${customerName} - Softphones`;
      }
    }

    return () => {
      window.removeEventListener('loginAsCustomerChanged', handleLoginAsChange as EventListener);
      // Reset title when component unmounts
      if (!isLoginAsCustomer) {
        document.title = 'Portal';
      }
    };
  }, [isLoginAsCustomer, member]);

  const fetchSoftphones = async () => {
    // SAFETY CHECK: Don't fetch if waiting for login-as data
    if (waitingForLoginAs) {
      logger.debug('Still waiting for login-as data, aborting fetch');
      return;
    }
    
    // Verify session data matches member if in login-as mode
    const sessionData = sessionStorage.getItem('loginAsCustomer');
    if (sessionData) {
      try {
        const parsed = JSON.parse(sessionData);
        if (parsed.customerId && parsed.customerId !== member?.customerID) {
          logger.critical('CRITICAL: Customer ID mismatch!', {
            sessionCustomerId: parsed.customerId,
            memberCustomerId: member?.customerID
          });
          setError('Customer context mismatch - please refresh the page');
          setLoading(false);
          return;
        }
      } catch (e) {
        logger.error('Error parsing session data:', e);
      }
    }
    
    try {
      setLoading(true);
      
      logger.debug('Fetching with customer context:', {
        isLoginAsCustomer,
        memberCustomerID: member?.customerID
      });
      
      // Use customer params helper
      const params = getCustomerParams({
        isLoginAsCustomer,
        memberCustomerID: member?.customerID,
        customerFilterIsFiltered: customerFilter.isFiltered,
        customerFilterCustomerId: customerFilter.customerId,
        memberType: member?.memberType
      });
      
      const response = await api.get('/endpoints/softphones', { params });
      if (response.data.success) {
        const softphonesData = response.data.data?.softphones || [];
        setSoftphones(softphonesData);
        
        // Also calculate stats from the received data
        if (softphonesData.length > 0) {
          // Calculate connected devices by counting devices that are actually online/active
          let connectedDevices = 0;
          softphonesData.forEach((s: SoftphoneUser) => {
            if (s.devices && Array.isArray(s.devices)) {
              // Count devices that have been seen recently (within last hour) or have active state
              connectedDevices += s.devices.filter((d: Device) => d.active).length;
            } else if (s.has_softphone && s.state && s.state > 0) {
              // Fallback: if no device details, count as 1 if softphone is active
              connectedDevices += 1;
            }
          });
          
          const calculatedStats = {
            total_extensions: softphonesData.length,
            total_softphones: softphonesData.filter((s: SoftphoneUser) => s.has_softphone).length,
            active_softphones: softphonesData.filter((s: SoftphoneUser) => s.has_softphone && !s.disabled).length,
            connected_devices: connectedDevices
          };
          setStats(calculatedStats);
        }
      }
      setError(null);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load softphones');
      logger.error('Softphones error:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await api.get('/endpoints/softphones/stats');
      if (response.data.success) {
        const statsData = response.data.data || response.data;
        setStats(statsData);
      }
    } catch (err: any) {
      logger.error('Stats error:', err);
      // If stats fail, calculate from local data
      if (softphones.length > 0) {
        const calculatedStats = {
          total_extensions: softphones.length,
          total_softphones: softphones.filter(s => s.has_softphone).length,
          active_softphones: softphones.filter(s => s.has_softphone && !s.disabled).length,
          connected_devices: softphones.reduce((sum, s) => sum + (s.device_count || 0), 0)
        };
        setStats(calculatedStats);
      }
    }
  };

  const handleToggleSoftphone = async (user: SoftphoneUser, billingConfirmed: boolean = false) => {
    if (!user.has_softphone) {
      // If no softphone exists, open the reset password dialog (which handles creation)
      setSelectedUser(user);
      setResetPasswordOpen(true);
      return;
    }
    
    // Use username, ringotel_id, or construct from customer_id and extension
    const identifier = user.username || user.ringotel_id || `${user.customer_id}_${user.extension}`;
    
    logger.debug('Toggle softphone:', {
      user,
      identifier,
      disabled: user.disabled,
      enabled: user.disabled ? true : false,
      billingConfirmed
    });
    
    // Get customer params for API call
    const params = getCustomerParams({
      isLoginAsCustomer,
      memberCustomerID: member?.customerID,
      customerFilterIsFiltered: customerFilter.isFiltered,
      customerFilterCustomerId: customerFilter.customerId,
      memberType: member?.memberType
    });
    
    try {
      logger.debug(`Making API call to: /endpoints/softphones/${identifier}/toggle`);
      const response = await api.put(`/endpoints/softphones/${identifier}/toggle`, {
        enabled: user.disabled ? true : false,
        billing_confirmed: billingConfirmed,
        user: member // Pass user context for billing
      }, { params });
      
      logger.debug('API response:', response);
      
      if (response.data.success) {
        // Check if we need billing confirmation
        if (response.data.requires_billing_confirmation && response.data.charge_preview) {
          // Store the user for later use after billing confirmation
          setSelectedUser(user);
          setChargeDetails(response.data.charge_preview);
          setChargePreviewOpen(true);
          return;
        }
        
        // Success - softphone toggled
        setSuccess(`Softphone ${user.disabled ? 'enabled' : 'disabled'} successfully`);
        fetchSoftphones();
      } else {
        logger.error('API returned success=false:', response.data);
        setError(response.data.message || 'Failed to toggle softphone');
      }
    } catch (err: any) {
      logger.error('Toggle error:', err);
      logger.error('Error response:', err.response);
      setError(err.response?.data?.message || err.message || 'Failed to toggle softphone');
    }
  };

  const handleResetPassword = async () => {
    if (!selectedUser) return;
    
    try {
      // Check if user has a softphone
      if (!selectedUser.has_softphone) {
        // Get customer params for API call
        const params = getCustomerParams({
          isLoginAsCustomer,
          memberCustomerID: member?.customerID,
          customerFilterIsFiltered: customerFilter.isFiltered,
          customerFilterCustomerId: customerFilter.customerId,
          memberType: member?.memberType
        });
        
        // Create a softphone first if none exists
        const createResponse = await api.post('/endpoints/softphones', {
          extension: selectedUser.extension,
          customer_id: selectedUser.customer_id,
          send_email: true
        }, { params });
        
        if (createResponse.data.success) {
          setSuccess('Softphone created and password sent to user email.');
          setResetPasswordOpen(false);
          setSelectedUser(null);
          // Refresh the list to show the new softphone
          fetchSoftphones();
        } else {
          // Handle API returning success: false
          setError(createResponse.data.message || 'Failed to create softphone');
          logger.error('Create softphone failed:', createResponse.data);
        }
      } else {
        // Get customer params for API call
        const params = getCustomerParams({
          isLoginAsCustomer,
          memberCustomerID: member?.customerID,
          customerFilterIsFiltered: customerFilter.isFiltered,
          customerFilterCustomerId: customerFilter.customerId,
          memberType: member?.memberType
        });
        
        // Check if softphone is disabled
        if (selectedUser.disabled) {
          // First enable the softphone using the username (which could be the actual username or Ringotel ID)
          const enableResponse = await api.put(`/endpoints/softphones/${selectedUser.username || selectedUser.ringotel_id}/toggle`, {
            enabled: true
          }, { params });
          
          if (!enableResponse.data.success) {
            setError('Failed to enable softphone');
            return;
          }
        }
        
        // Reset password for existing softphone
        // Include extension and customer_id in the request body as fallback
        const response = await api.post(`/endpoints/softphones/${selectedUser.username || selectedUser.ringotel_id}/reset-password`, {
          send_email: true,
          extension: selectedUser.extension,
          customer_id: selectedUser.customer_id
        }, { params });
        
        if (response.data.success) {
          setSuccess(selectedUser.disabled ? 
            'Softphone enabled and password reset successfully. Email sent to user.' : 
            'Password reset successfully. Email sent to user.');
          setResetPasswordOpen(false);
          setSelectedUser(null);
          // Refresh the list if we enabled a disabled softphone
          if (selectedUser.disabled) {
            fetchSoftphones();
          }
        }
      }
    } catch (err: any) {
      logger.error('handleResetPassword error:', err);
      logger.error('Error response:', err.response);
      setError(err.response?.data?.message || err.message || 'Failed to process request');
      // Don't close the dialog on error so user can see the message
    }
  };

  const handleAddSoftphone = async (extension: string, force: boolean = false) => {
    try {
      setError(null);
      setChargePreviewError(null);
      setPendingExtension(extension);
      setPendingForce(force);
      
      // Get customer params for API call
      const params = getCustomerParams({
        isLoginAsCustomer,
        memberCustomerID: member?.customerID,
        customerFilterIsFiltered: customerFilter.isFiltered,
        customerFilterCustomerId: customerFilter.customerId,
        memberType: member?.memberType
      });
      
      // First, check if there's a user assigned to this extension
      const customerId = params.customerId || member?.customerID;
      const extensionResponse = await api.get(`/endpoints/extensions`, { params });
      const extensions = extensionResponse.data.data || [];
      const extensionData = extensions.find((ext: any) => ext.extension === extension);
      
      if (!extensionData || !extensionData.member_id) {
        setError(`Extension ${extension} does not have a user assigned. Please assign a user to this extension before creating a softphone.`);
        return;
      }
      
      // Next, check if softphones are included in the package
      const billingResponse = await api.get('/endpoints/billing/package-info');
      const packageInfo = billingResponse.data.data;
      
      let shouldCharge = true;
      let monthlyRate = 14.95; // Default softphone rate
      
      if (packageInfo && packageInfo.package) {
        // Check if softphones are included (note: API returns camelCase)
        if (packageInfo.package.softphoneIncluded) {
          shouldCharge = false;
        } else if (packageInfo.package.softphoneRate) {
          monthlyRate = parseFloat(packageInfo.package.softphoneRate);
        }
      }
      
      if (!shouldCharge) {
        // Softphone is included in package, no charge needed
        const response = await api.post('/endpoints/softphones', {
          extension,
          customer_id: member?.customerID,
          force
        }, { params });
        
        if (response.data.success) {
          let msg = force ? 'Softphone recreated successfully' : 'Softphone created successfully';
          if (response.data.included_in_package) {
            msg += ' (Included in your package - no charge)';
          }
          setSuccess(msg);
          setAddDialogOpen(false);
          fetchSoftphones();
        }
        return;
      }
      
      // Get charge preview (reuse customerId from above)
      const previewResponse = await api.post('/endpoints/charge-preview', {
        service_type: 'softphone',
        service_id: extension,
        monthly_rate: monthlyRate,
        customerId: customerId
      }, { params });
      
      if (previewResponse.data.success) {
        // Map snake_case API response to camelCase for component
        const apiData = previewResponse.data.data;
        setChargeDetails({
          serviceType: apiData.service_type,
          serviceId: apiData.service_id,
          description: apiData.description,
          monthlyRate: apiData.monthly_rate,
          proratedDays: apiData.prorated_days,
          proratedAmount: apiData.prorated_amount,
          currentMonthCharge: apiData.current_month_charge,
          nextMonthCharge: apiData.next_month_charge,
          subtotal: apiData.subtotal,
          taxDetails: apiData.tax_details,
          totalTax: apiData.total_tax,
          totalCharge: apiData.total_charge,
          currentBalance: apiData.current_balance,
          newBalance: apiData.new_balance,
          sufficientFunds: apiData.sufficient_funds,
          billingPeriod: apiData.billing_period,
          includedInPackage: apiData.included_in_package,
          province: apiData.province,
          provinceName: apiData.province_name
        });
        setChargePreviewOpen(true);
        setAddDialogOpen(false);
      }
    } catch (err: any) {
      if (err.response?.status === 409) {
        // Softphone already exists in the system, ask if they want to recreate
        if (window.confirm('A softphone already exists for this extension. Do you want to recreate it? This will generate a new password.')) {
          handleAddSoftphone(extension, true); // Retry with force=true
        }
      } else {
        setError(err.response?.data?.message || 'Failed to create softphone');
      }
    }
  };

  const handleDeleteSoftphone = async () => {
    if (!selectedUser) return;
    
    // Use username if available, otherwise construct from customer_id and extension
    const identifier = selectedUser.username || `${selectedUser.customer_id}_${selectedUser.extension}`;
    
    logger.debug(`Deleting softphone with identifier: ${identifier}`, { userData: selectedUser });
    
    // Get customer params for API call
    const params = getCustomerParams({
      isLoginAsCustomer,
      memberCustomerID: member?.customerID,
      customerFilterIsFiltered: customerFilter.isFiltered,
      customerFilterCustomerId: customerFilter.customerId,
      memberType: member?.memberType
    });
    
    try {
      const response = await api.delete(`/endpoints/softphones/${identifier}`, { params });
      
      if (response.data.success) {
        setSuccess('Softphone deleted successfully');
        setDeleteDialogOpen(false);
        setSelectedUser(null);
        fetchSoftphones();
      }
    } catch (err: any) {
      logger.error('Delete error:', err);
      setError(err.response?.data?.message || 'Failed to delete softphone');
    }
  };

  const handleChargePreviewConfirm = async () => {
    try {
      setChargePreviewLoading(true);
      setChargePreviewError(null);
      
      // Check if this is for enabling a softphone (toggle) or creating new
      if (selectedUser && chargeDetails?.service_type === 'softphone' && chargeDetails?.description?.includes('Enable')) {
        // This is for enabling an existing softphone
        await handleToggleSoftphone(selectedUser, true); // Call with billing confirmed
        setChargePreviewOpen(false);
        setChargeDetails(null);
        setSelectedUser(null);
      } else {
        // Get customer params for API call
        const params = getCustomerParams({
          isLoginAsCustomer,
          memberCustomerID: member?.customerID,
          customerFilterIsFiltered: customerFilter.isFiltered,
          customerFilterCustomerId: customerFilter.customerId,
          memberType: member?.memberType
        });
        
        // This is for creating a new softphone
        const response = await api.post('/endpoints/softphones', {
          extension: pendingExtension,
          customer_id: member?.customerID,
          force: pendingForce,
          charge_approved: true // Indicates user approved the charge preview
        }, { params });
        
        if (response.data.success) {
          let msg = pendingForce ? 'Softphone recreated successfully' : 'Softphone created successfully';
          
          if (chargeDetails?.includedInPackage) {
            msg += ' (Included in your package - no charge)';
          } else if (response.data.billed) {
            msg += ` - Charged ${new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(response.data.amount_charged || chargeDetails?.totalCharge || 0)}`;
          }
          
          setSuccess(msg);
          setChargePreviewOpen(false);
          setChargeDetails(null);
          setPendingExtension('');
          setPendingForce(false);
          fetchSoftphones();
        } else {
          setChargePreviewError(response.data.message || 'Failed to create softphone');
        }
      }
    } catch (err: any) {
      setChargePreviewError(err.response?.data?.message || 'Failed to process softphone request');
    } finally {
      setChargePreviewLoading(false);
    }
  };

  const handleChargePreviewClose = () => {
    setChargePreviewOpen(false);
    setChargeDetails(null);
    setChargePreviewError(null);
    setPendingExtension('');
    setPendingForce(false);
    setSelectedUser(null);
  };

  const handleAddFunds = () => {
    setChargePreviewOpen(false);
    navigate('/billing');
  };

  const handleViewDetails = async (user: SoftphoneUser) => {
    setSelectedUser(user);
    setDetailsOpen(true);
    
    // Fetch detailed information if username exists
    if (user.username) {
      try {
        const response = await api.get(`/endpoints/softphones/${user.username}/details`);
        if (response.data.success && response.data.data) {
          // Only update if we got actual data back
          setSelectedUser(prevUser => ({
            ...prevUser!,
            ...response.data.data
          }));
        }
      } catch (err) {
        logger.error('Failed to fetch details:', err);
        // Keep the existing user data on error
      }
    }
  };

  const getStateIcon = (user: SoftphoneUser) => {
    if (!user.has_softphone) {
      return <InfoIcon color="disabled" />;
    }
    
    switch (user.state) {
      case 1: // Online
      case 5: // Available on PBX
        return <ActiveIcon color="success" />;
      case 2: // Away
        return <InactiveIcon color="warning" />;
      case 3: // Busy (DND)
        return <InactiveIcon color="error" />;
      case 4: // At The Desk
      case 8: // Connected
        return <ActiveIcon color="primary" />;
      case 6: // Calling
      case 7: // Calling
        return <PhoneIcon color="warning" />;
      case 0: // Offline
      case -1: // Unavailable
        return <InactiveIcon color="disabled" />;
      default:
        return <InfoIcon color="disabled" />;
    }
  };

  const filteredSoftphones = softphones.filter(user =>
    user.extension.includes(searchTerm) ||
    user.extension_desc?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.customer_name?.toLowerCase().includes(searchTerm.toLowerCase())
  );
  
  // Listen for user deletion events from other components
  useEffect(() => {
    const handleUserDeleted = (event: Event) => {
      logger.debug('User deleted event received in Softphones, refreshing...');
      // Refresh softphones to update user information
      if (member && ['admin', 'master_admin', 'reseller_admin'].includes(member.memberType)) {
        fetchSoftphones();
      }
    };
    
    const handleStorageChange = (event: StorageEvent) => {
      if (event.key === 'userDeleted' && event.newValue) {
        logger.debug('User deleted via storage event, refreshing softphones...');
        if (member && ['admin', 'master_admin', 'reseller_admin'].includes(member.memberType)) {
          fetchSoftphones();
        }
      }
    };
    
    // Listen for both custom events and storage events
    window.addEventListener('userDeleted', handleUserDeleted);
    window.addEventListener('storage', handleStorageChange);
    
    return () => {
      window.removeEventListener('userDeleted', handleUserDeleted);
      window.removeEventListener('storage', handleStorageChange);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [member]);

  if (member && !['admin', 'master_admin', 'reseller_admin'].includes(member.memberType)) {
    return (
      <Alert severity="error">
        You do not have permission to access this section.
      </Alert>
    );
  }

  return (
    <Box>
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Typography variant="h4" component="h1">
            Softphone Management
          </Typography>
          <Box>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={() => {
                fetchSoftphones();
              }}
              sx={{ mr: 2 }}
            >
              Refresh
            </Button>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setAddDialogOpen(true)}
            >
              Add Softphone
            </Button>
          </Box>
        </Box>

        {/* Statistics Cards */}
        <Grid container spacing={3} sx={{ mb: 3, alignItems: 'stretch' }}>
          <Grid item xs={12} sm={6} md={3} sx={{ display: 'flex' }}>
            <Card sx={{ height: '100%', minHeight: 150 }}>
              <CardContent sx={{ height: '100%', display: 'flex', alignItems: 'center', p: 3 }}>
                <Box display="flex" alignItems="center">
                  <Avatar sx={{ bgcolor: theme.palette.primary.main, mr: 2, width: 56, height: 56 }}>
                    <PhoneIcon sx={{ fontSize: 28 }} />
                  </Avatar>
                  <Box>
                    <Typography color="textSecondary" gutterBottom variant="body2">
                      Total Extensions
                    </Typography>
                    <Typography variant="h4">
                      {stats.total_extensions}
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3} sx={{ display: 'flex' }}>
            <Card sx={{ height: '100%', minHeight: 150 }}>
              <CardContent sx={{ height: '100%', display: 'flex', alignItems: 'center', p: 3 }}>
                <Box display="flex" alignItems="center">
                  <Avatar sx={{ bgcolor: theme.palette.info.main, mr: 2, width: 56, height: 56 }}>
                    <PhoneIcon sx={{ fontSize: 28 }} />
                  </Avatar>
                  <Box>
                    <Typography color="textSecondary" gutterBottom variant="body2">
                      Total Softphones
                    </Typography>
                    <Typography variant="h4">
                      {stats.total_softphones}
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3} sx={{ display: 'flex' }}>
            <Card sx={{ height: '100%', minHeight: 150 }}>
              <CardContent sx={{ height: '100%', display: 'flex', alignItems: 'center', p: 3 }}>
                <Box display="flex" alignItems="center">
                  <Avatar sx={{ bgcolor: theme.palette.success.main, mr: 2, width: 56, height: 56 }}>
                    <ActiveIcon sx={{ fontSize: 28 }} />
                  </Avatar>
                  <Box>
                    <Typography color="textSecondary" gutterBottom variant="body2">
                      Active Softphones
                    </Typography>
                    <Typography variant="h4">
                      {stats.active_softphones}
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3} sx={{ display: 'flex' }}>
            <Card sx={{ height: '100%', minHeight: 150 }}>
              <CardContent sx={{ height: '100%', display: 'flex', alignItems: 'center', p: 3 }}>
                <Box display="flex" alignItems="center">
                  <Avatar sx={{ bgcolor: theme.palette.warning.main, mr: 2, width: 56, height: 56 }}>
                    <DeviceIcon sx={{ fontSize: 28 }} />
                  </Avatar>
                  <Box>
                    <Typography color="textSecondary" gutterBottom variant="body2">
                      Connected Devices
                    </Typography>
                    <Typography variant="h4">
                      {stats.connected_devices}
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Search Bar */}
        <TextField
          fullWidth
          variant="outlined"
          placeholder="Search by extension, name, email, or customer..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          sx={{ mb: 3 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />

        {/* Error/Success Messages */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
            {success}
          </Alert>
        )}

        {/* Softphones Table/List */}
        {loading ? (
          <Box display="flex" justifyContent="center" p={4}>
            <CircularProgress />
          </Box>
        ) : isMobile ? (
          // Mobile Card View
          <List>
            {filteredSoftphones.map((user) => (
              <Card key={`${user.extension}-${user.id}`} sx={{ mb: 2 }}>
                <CardContent>
                  <Box display="flex" alignItems="center" mb={2}>
                    {getStateIcon(user)}
                    <Typography variant="h6" sx={{ ml: 1 }}>
                      {user.extension} - {user.extension_desc || 'Unnamed'}
                    </Typography>
                  </Box>
                  <Typography variant="body2" color="textSecondary">
                    Name: {user.extension_desc || 'Unnamed'}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Email: {user.email || 'No email'}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Customer: {user.customer_name}
                  </Typography>
                  <Box display="flex" alignItems="center" mt={1}>
                    <Chip
                      label={!user.has_softphone ? 'No Softphone' : user.disabled ? 'Disabled' : 'Active'}
                      color={!user.has_softphone ? 'default' : user.disabled ? 'error' : 'success'}
                      size="small"
                    />
                    {user.device_count && user.device_count > 0 && (
                      <Box display="inline-flex" alignItems="center" sx={{ ml: 2 }}>
                        <DeviceIcon fontSize="small" sx={{ mr: 0.5 }} />
                        <Typography variant="body2">
                          {user.devices && user.devices.length > 0 
                            ? `${user.devices.filter((d: Device) => d.active).length}/${user.device_count}`
                            : user.device_count}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </CardContent>
                <CardActions>
                  <Button
                    size="small"
                    onClick={() => handleViewDetails(user)}
                  >
                    Details
                  </Button>
                  <Button
                    size="small"
                    onClick={() => handleToggleSoftphone(user)}
                    color={!user.has_softphone ? 'primary' : user.disabled ? 'error' : 'success'}
                  >
                    {!user.has_softphone ? 'Add Softphone' : user.disabled ? 'Enable' : 'Disable'}
                  </Button>
                  {user.has_softphone && (
                    <>
                      <Button
                        size="small"
                        onClick={() => {
                          setSelectedUser(user);
                          setResetPasswordOpen(true);
                        }}
                      >
                        Reset Password
                      </Button>
                      <Button
                        size="small"
                        color="error"
                        onClick={() => {
                          setSelectedUser(user);
                          setDeleteDialogOpen(true);
                        }}
                      >
                        Delete
                      </Button>
                    </>
                  )}
                </CardActions>
              </Card>
            ))}
          </List>
        ) : (
          // Desktop Table View
          <TableContainer sx={{ overflowX: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ minWidth: 100 }}>Status</TableCell>
                  <TableCell sx={{ minWidth: 70 }}>Ext</TableCell>
                  <TableCell sx={{ minWidth: 120 }}>Name</TableCell>
                  <TableCell sx={{ minWidth: 150 }}>Email</TableCell>
                  <TableCell sx={{ minWidth: 60 }}>Devices</TableCell>
                  <TableCell sx={{ minWidth: 120 }}>Last Activity</TableCell>
                  <TableCell align="right" sx={{ minWidth: 120 }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredSoftphones.map((user) => (
                  <TableRow key={`${user.extension}-${user.id}`}>
                    <TableCell>
                      <Chip
                        label={user.state_text || 'Unknown'}
                        size="small"
                        sx={{
                          bgcolor: 
                            user.state === 1 || user.state === 5 ? 'success.light' :
                            user.state === 2 ? 'warning.light' :
                            user.state === 3 ? 'error.light' :
                            user.state === 4 || user.state === 8 ? 'info.light' :
                            user.state === 6 || user.state === 7 ? 'warning.light' :
                            'grey.300',
                          color:
                            user.state === 1 || user.state === 5 ? 'success.dark' :
                            user.state === 2 ? 'warning.dark' :
                            user.state === 3 ? 'error.dark' :
                            user.state === 4 || user.state === 8 ? 'info.dark' :
                            user.state === 6 || user.state === 7 ? 'warning.dark' :
                            'text.secondary',
                          fontWeight: 500
                        }}
                      />
                    </TableCell>
                    <TableCell>{user.extension}</TableCell>
                    <TableCell>
                      <Typography variant="body2" noWrap sx={{ maxWidth: 150 }}>
                        {user.extension_desc || 'Unnamed'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" noWrap sx={{ maxWidth: 180 }}>
                        {user.email || 'No email'}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      {user.devices && user.devices.length > 0 ? (
                        <Tooltip title={`${user.devices.filter((d: Device) => d.active).length} active out of ${user.device_count || 0} total registered devices`}>
                          <Box sx={{ cursor: 'help' }}>
                            <Typography variant="body2">
                              {user.devices.filter((d: Device) => d.active).length}/{user.device_count || 0}
                            </Typography>
                            {user.devices.filter((d: Device) => d.active).length > 0 && (
                              <Typography variant="caption" color="success.main">
                                active
                              </Typography>
                            )}
                          </Box>
                        </Tooltip>
                      ) : (
                        '0'
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" noWrap>
                        {user.last_activity ? 
                          new Date(user.last_activity).toLocaleDateString('en-US', { 
                            month: 'short', 
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          }) : 'Never'}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Box display="flex" justifyContent="flex-end" gap={0.5}>
                        <Tooltip title="View Details">
                          <IconButton
                            size="small"
                            onClick={() => handleViewDetails(user)}
                            sx={{ padding: 0.5 }}
                          >
                            <InfoIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={!user.has_softphone ? 'Add Softphone' : user.disabled ? 'Enable' : 'Disable'}>
                          <IconButton
                            size="small"
                            onClick={() => handleToggleSoftphone(user)}
                            color={!user.has_softphone ? 'primary' : user.disabled ? 'error' : 'success'}
                            sx={{ padding: 0.5 }}
                          >
                            {!user.has_softphone ? <AddIcon fontSize="small" /> : <PowerIcon fontSize="small" />}
                          </IconButton>
                        </Tooltip>
                        {user.has_softphone && (
                          <>
                            <Tooltip title="Reset Password">
                              <IconButton
                                size="small"
                                onClick={() => {
                                  setSelectedUser(user);
                                  setResetPasswordOpen(true);
                                }}
                                sx={{ padding: 0.5 }}
                              >
                                <PasswordIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete Softphone">
                              <IconButton
                                size="small"
                                onClick={() => {
                                  setSelectedUser(user);
                                  setDeleteDialogOpen(true);
                                }}
                                color="error"
                                sx={{ padding: 0.5 }}
                              >
                                <InactiveIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {/* Details Dialog */}
      <Dialog
        open={detailsOpen}
        onClose={() => setDetailsOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Softphone Details - {selectedUser?.extension}
        </DialogTitle>
        <DialogContent>
          {selectedUser && (
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="textSecondary">
                  Extension
                </Typography>
                <Typography>{selectedUser.extension}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="textSecondary">
                  Name
                </Typography>
                <Typography>{selectedUser.extension_desc || 'Unnamed'}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="textSecondary">
                  Email
                </Typography>
                <Typography>{selectedUser.email || 'No email'}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="textSecondary">
                  Status
                </Typography>
                <Chip
                  label={selectedUser.state_text || 'Unknown'}
                  color={selectedUser.state === 4 ? 'success' : 'default'}
                  size="small"
                />
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                  Devices ({selectedUser.device_count || 0})
                </Typography>
                {selectedUser.devices && selectedUser.devices.length > 0 ? (
                  <List dense>
                    {selectedUser.devices.map((device) => (
                      <ListItem key={device.id}>
                        <ListItemAvatar>
                          <Avatar>
                            <DeviceIcon />
                          </Avatar>
                        </ListItemAvatar>
                        <ListItemText
                          primary={device.name}
                          secondary={`Type: ${device.type} | Last seen: ${device.last_seen}`}
                        />
                        <ListItemSecondaryAction>
                          <Chip
                            label={device.active ? 'Active' : 'Inactive'}
                            color={device.active ? 'success' : 'default'}
                            size="small"
                          />
                        </ListItemSecondaryAction>
                      </ListItem>
                    ))}
                  </List>
                ) : (
                  <Typography color="textSecondary">No devices connected</Typography>
                )}
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailsOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Reset Password Dialog */}
      <Dialog
        open={resetPasswordOpen}
        onClose={() => setResetPasswordOpen(false)}
      >
        <DialogTitle>
          {selectedUser?.has_softphone ? 
            (selectedUser?.disabled ? 'Enable & Reset Softphone' : 'Reset Softphone Password') : 
            'Create Softphone'}
        </DialogTitle>
        <DialogContent>
          <Typography>
            {selectedUser?.has_softphone ? (
              selectedUser?.disabled ? (
                `Extension ${selectedUser?.extension} has a disabled softphone. Would you like to enable it and reset the password?`
              ) : (
                `Are you sure you want to reset the password for extension ${selectedUser?.extension}?`
              )
            ) : (
              `Extension ${selectedUser?.extension} does not have a softphone configured. Would you like to create a softphone for this extension?`
            )}
          </Typography>
          <Typography variant="body2" color="textSecondary" sx={{ mt: 2 }}>
            {selectedUser?.has_softphone ? (
              selectedUser?.disabled ? (
                `The softphone will be enabled and new login credentials will be sent to ${selectedUser?.email || 'the user'}.`
              ) : (
                `An email with the new password will be sent to ${selectedUser?.email || 'the user'}.`
              )
            ) : (
              `Login credentials will be sent to ${selectedUser?.email || 'the user'}.`
            )}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResetPasswordOpen(false)}>Cancel</Button>
          <Button
            onClick={handleResetPassword}
            variant="contained"
            color="primary"
          >
            {selectedUser?.has_softphone ? 
              (selectedUser?.disabled ? 'Enable & Reset' : 'Reset Password') : 
              'Create Softphone'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Softphone Dialog */}
      <Dialog
        open={addDialogOpen}
        onClose={() => setAddDialogOpen(false)}
      >
        <DialogTitle>Add Softphone</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Extension Number"
            fullWidth
            variant="outlined"
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                handleAddSoftphone((e.target as HTMLInputElement).value, false);
              }
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={() => {
              const input = document.querySelector('input[type="text"]') as HTMLInputElement;
              if (input) {
                handleAddSoftphone(input.value, false);
              }
            }}
            variant="contained"
            color="primary"
          >
            Add
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Softphone Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>Delete Softphone</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the softphone for extension {selectedUser?.extension}?
          </Typography>
          <Typography variant="body2" color="error" sx={{ mt: 2 }}>
            This action cannot be undone. The user will lose access to their softphone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleDeleteSoftphone}
            variant="contained"
            color="error"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Charge Preview Dialog */}
      <ChargePreviewDialog
        open={chargePreviewOpen}
        onClose={handleChargePreviewClose}
        onConfirm={handleChargePreviewConfirm}
        onAddFunds={handleAddFunds}
        chargeDetails={chargeDetails}
        loading={chargePreviewLoading}
        error={chargePreviewError}
      />
    </Box>
  );
};

export default Softphones;